import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Switch, Route, Redirect, useHistory } from 'react-router-dom';
import { Layout, Menu, Breadcrumb, Spin } from '@arco-design/web-react';
import cs from 'classnames';
import {
  IconDashboard,
  IconList,
  IconSettings,
  IconFile,
  IconApps,
  IconCheckCircle,
  IconExclamationCircle,
  IconUser,
  IconMenuFold,
  IconMenuUnfold,
  IconStorage,
  IconRobot,
  IconQuestionCircle,
  IconSafe,
} from '@arco-design/web-react/icon';
import { useSelector } from 'react-redux';
import qs from 'query-string';
import NProgress from 'nprogress';
import Navbar from './components/NavBar';
import Footer from './components/Footer';
import useRoute, { IRoute } from '@/routes';
import { isArray } from '@gbi/shared/utils/common';
import useLocale from './utils/useLocale';
import getUrlParams from './utils/getUrlParams';
import lazyload from './utils/lazyload';
import { GlobalState } from './store';
import styles from './style/layout.module.less';
import PageTitle from './components/PageTitle';

const MenuItem = Menu.Item;
const SubMenu = Menu.SubMenu;

const Sider = Layout.Sider;
const Content = Layout.Content;

function getIconFromKey(key) {
  switch (key) {
    case 'dashboard':
      return <IconDashboard className={styles.icon} />;
    case 'list':
      return <IconList className={styles.icon} />;
    case 'form':
      return <IconSettings className={styles.icon} />;
    case 'profile':
      return <IconFile className={styles.icon} />;
    case 'visualization':
      return <IconApps className={styles.icon} />;
    case 'result':
      return <IconCheckCircle className={styles.icon} />;
    case 'exception':
      return <IconExclamationCircle className={styles.icon} />;
    case 'user':
      return <IconUser className={styles.icon} />;
    case 'content-source':
      return <IconStorage className={styles.icon} />;
    case 'data-intelligence':
      return <IconRobot className={styles.icon} />;
    case 'medical-data':
      return <IconStorage className={styles.icon} />;
    case 'help-center':
      return <IconQuestionCircle className={styles.icon} />;
    case 'nhsa':
      return <IconSafe className={styles.icon} />;
    default:
      return <div className={styles['icon-empty']} />;
  }
}

function getFlattenRoutes(routes) {
  const res = [];

  // 定义一个帮助函数来处理路由导入
  function getRouteComponent(key) {
    // 根据路由key手动映射
    switch (key) {
      case 'dashboard/workplace':
        return lazyload(() => import('./pages/dashboard/workplace/index'));
      case 'dashboard/statistics':
        return lazyload(() => import('./pages/dashboard/statistics/index'));
      case 'dashboard/drug-sales-statistics':
        return lazyload(
          () => import('./pages/dashboard/drug-sales-statistics/index')
        );
      case 'content-source/policy':
        return lazyload(() => import('./pages/content-source/policy/index'));
      case 'content-source/policy-detail/:uuid':
        return lazyload(() => import('./pages/content-source/policy-detail/index'));
      case 'content-source/policy-compare':
        return lazyload(() => import('./pages/content-source/policy-compare/index'));
      case 'content-source/subscription':
        return lazyload(
          () => import('./pages/content-source/subscription/index')
        );
      case 'content-source/favorite':
        return lazyload(() => import('./pages/content-source/favorite/index'));
      case 'content-source/knowledge-base':
        return lazyload(
          () => import('./pages/content-source/knowledge-base/index')
        );
      case 'data-intelligence/policy-qa':
        return lazyload(
          () => import('./pages/data-intelligence/policy-qa/index')
        );
      case 'data-intelligence/deep-search':
        return lazyload(
          () => import('./pages/data-intelligence/deep-search/index')
        );
      case 'data-intelligence/report-waiting':
        return lazyload(
          () => import('./pages/data-intelligence/report-waiting/index')
        );
      case 'user/setting':
        return lazyload(() => import('./pages/user/setting/index'));
      case 'change-log':
        return lazyload(() => import('./pages/change-log/index'));
      case 'terms-of-service':
        return lazyload(() => import('./pages/terms-of-service/index'));
      case 'privacy-policy':
        return lazyload(() => import('./pages/privacy-policy/index'));
      case 'help-center':
        return lazyload(() => import('./pages/help-center/index'));
      // NHSA 医保数据
      case 'nhsa/drug-catalog':
        return lazyload(() => import('./pages/nhsa/drug-catalog/index'));
      case 'nhsa/drug-allocation':
        return lazyload(() => import('./pages/nhsa/drug-allocation/index'));
      case 'nhsa/medical-supplies':
        return lazyload(() => import('./pages/nhsa/medical-supplies/index'));
      case 'nhsa/hospital':
        return lazyload(() => import('./pages/nhsa/hospital/index'));
      case 'nhsa/shop':
        return lazyload(() => import('./pages/nhsa/shop/index'));
      default:
        console.warn(`未找到路由组件: ${key}`);
        return null;
    }
  }

  function travel(_routes) {
    _routes.forEach((route) => {
      const visibleChildren = (route.children || []).filter(
        (child) => !child.ignore
      );
      if (route.key && (!route.children || !visibleChildren.length)) {
        try {
          const component = getRouteComponent(route.key);
          if (component) {
            route.component = component;
            res.push(route);
          }
        } catch (e) {
          console.log('路由加载错误:', route.key);
          console.error(e);
        }
      }

      if (isArray(route.children) && route.children.length) {
        travel(route.children);
      }
    });
  }
  travel(routes);
  return res;
}

function PageLayout() {
  const urlParams = getUrlParams();
  const history = useHistory();
  const pathname = history.location.pathname;
  const currentComponent = qs.parseUrl(pathname).url.slice(1);
  const locale = useLocale();
  const { settings, userLoading, userInfo } = useSelector(
    (state: GlobalState) => state
  );

  const [routes, defaultRoute] = useRoute(userInfo?.permissions);
  const defaultSelectedKeys = [currentComponent || defaultRoute];
  const paths = (currentComponent || defaultRoute).split('/');
  const defaultOpenKeys = paths.slice(0, paths.length - 1);

  const [breadcrumb, setBreadCrumb] = useState([]);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [selectedKeys, setSelectedKeys] =
    useState<string[]>(defaultSelectedKeys);
  const [openKeys, setOpenKeys] = useState<string[]>(defaultOpenKeys);

  const routeMap = useRef<Map<string, React.ReactNode[]>>(new Map());
  const menuMap = useRef<
    Map<string, { menuItem?: boolean; subMenu?: boolean }>
  >(new Map());

  const navbarHeight = 60;
  const menuWidth = collapsed ? 48 : settings.menuWidth;

  const showNavbar = settings.navbar && urlParams.navbar !== false;
  const showMenu = settings.menu && urlParams.menu !== false;
  const showFooter = settings.footer && urlParams.footer !== false;

  const flattenRoutes = useMemo(() => getFlattenRoutes(routes) || [], [routes]);
  const [currentRoute, setCurrentRoute] = useState<IRoute | null>(null);

  function onClickMenuItem(key, event) {
    // 处理特殊页面的新窗口打开
    if (
      key === 'terms-of-service' ||
      key === 'privacy-policy' ||
      key === 'change-log'
    ) {
      window.open(`/${key}`, '_blank');
      return;
    }

    // 如果是鼠标中键点击，在新窗口打开
    if (event?.button === 1 || (event?.ctrlKey && event?.button === 0)) {
      const currentRoute = flattenRoutes.find((r) => r.key === key);
      if (currentRoute) {
        window.open(
          currentRoute.path ? currentRoute.path : `/${key}`,
          '_blank'
        );
      }
      return;
    }

    // 检查是否是政策问答页面
    if (key === 'data-intelligence/policy-qa') {
      // 如果有定义的全局函数，就调用它
      if (window.triggerPolicyQANavigation) {
        console.log('[导航] 准备重置政策问答页面状态');
        window.triggerPolicyQANavigation();
      }
    }

    // 常规点击处理
    const currentRoute = flattenRoutes.find((r) => r.key === key);

    if (!currentRoute) {
      console.warn(`未找到路由: ${key}`);
      return;
    }

    setCurrentRoute(currentRoute);
    const component = currentRoute.component;

    if (!component) {
      console.warn(`路由组件未找到: ${key}`);
      return;
    }

    const preload = component.preload();
    NProgress.start();
    preload.then(() => {
      history.push(currentRoute.path ? currentRoute.path : `/${key}`);
      NProgress.done();
    });
  }

  function toggleCollapse() {
    setCollapsed((collapsed) => !collapsed);
  }

  const paddingLeft = showMenu ? { paddingLeft: menuWidth } : {};
  const paddingTop = showNavbar ? { paddingTop: navbarHeight } : {};
  const paddingStyle = { ...paddingLeft, ...paddingTop };

  function renderRoutes(locale) {
    routeMap.current.clear();
    return function travel(_routes: IRoute[], level, parentNode = []) {
      return _routes.map((route) => {
        const { breadcrumb = true, ignore } = route;
        const iconDom = getIconFromKey(route.key);
        const titleDom = (
          <>
            {iconDom} {locale[route.name] || route.name}
          </>
        );

        routeMap.current.set(
          `/${route.key}`,
          breadcrumb ? [...parentNode, route.name] : []
        );

        const visibleChildren = (route.children || []).filter((child) => {
          const { ignore, breadcrumb = true } = child;
          if (ignore || route.ignore) {
            routeMap.current.set(
              `/${child.key}`,
              breadcrumb ? [...parentNode, route.name, child.name] : []
            );
          }

          return !ignore;
        });

        if (ignore) {
          return '';
        }
        if (visibleChildren.length) {
          menuMap.current.set(route.key, { subMenu: true });
          return (
            <SubMenu key={route.key} title={titleDom}>
              {travel(visibleChildren, level + 1, [...parentNode, route.name])}
            </SubMenu>
          );
        }
        menuMap.current.set(route.key, { menuItem: true });
        return (
          <MenuItem key={route.key}>
            {titleDom}
          </MenuItem>
        );
      });
    };
  }

  function updateMenuStatus() {
    const pathKeys = pathname.split('/');
    const newSelectedKeys: string[] = [];
    const newOpenKeys: string[] = [...openKeys];
    while (pathKeys.length > 0) {
      const currentRouteKey = pathKeys.join('/');
      const menuKey = currentRouteKey.replace(/^\//, '');
      const menuType = menuMap.current.get(menuKey);
      if (menuType && menuType.menuItem) {
        newSelectedKeys.push(menuKey);
      }
      if (menuType && menuType.subMenu && !openKeys.includes(menuKey)) {
        newOpenKeys.push(menuKey);
      }
      pathKeys.pop();
    }
    setSelectedKeys(newSelectedKeys);
    setOpenKeys(newOpenKeys);
  }

  useEffect(() => {
    const pathKeys = pathname.split('/');
    let currentRouteKey = pathKeys.join('/').replace(/^\//, '');
    if (currentRouteKey === '') {
      currentRouteKey = defaultRoute;
    }

    // 找到匹配的扁平化路由
    let currentFlatRoute = flattenRoutes.find(
      (r) => r.key === currentRouteKey
    );

    // 如果直接匹配失败，尝试匹配带动态参数的路由（如 :uuid）
    if (!currentFlatRoute) {
      currentFlatRoute = flattenRoutes.find((r) => {
        // 将路由 key 中的动态参数（如 :uuid）替换为正则表达式
        const routePattern = r.key.replace(/:[^/]+/g, '[^/]+');
        const regex = new RegExp(`^${routePattern}$`);
        return regex.test(currentRouteKey);
      });
    }

    // 如果没有直接匹配的路由，尝试匹配父子路由组合
    if (!currentFlatRoute && pathKeys.length > 1) {
      // 对于类似 content-source/policy 的路径，需要特殊处理
      for (const route of routes) {
        if (route.children) {
          const childRoute = route.children.find(
            (child) =>
              child.key === currentRouteKey ||
              `${route.key}/${child.key.split('/').pop()}` === currentRouteKey
          );
          if (childRoute) {
            setCurrentRoute({
              ...childRoute,
              // 如果没有设置title，则使用name作为标题
              title: childRoute.title || childRoute.name.split('.').pop(),
            });
            break;
          }
        }
      }
    } else if (currentFlatRoute) {
      setCurrentRoute(currentFlatRoute);
    }

    const routeConfig = routeMap.current.get(pathname);
    setBreadCrumb(routeConfig || []);
    updateMenuStatus();
  }, [pathname, defaultRoute, flattenRoutes, routes]);

  return (
    <Layout className={styles.layout}>
      {currentRoute && <PageTitle titleKey={currentRoute.name} />}
      <div
        className={cs(styles['layout-navbar'], {
          [styles['layout-navbar-hidden']]: !showNavbar,
        })}
      >
        <Navbar show={showNavbar} />
      </div>
      {userLoading ? (
        <Spin className={styles['spin']} />
      ) : (
        <Layout>
          {showMenu && (
            <Sider
              className={styles['layout-sider']}
              width={menuWidth}
              collapsed={collapsed}
              onCollapse={setCollapsed}
              trigger={null}
              collapsible
              breakpoint="xl"
              style={paddingTop}
            >
              <div className={styles['menu-wrapper']}>
                <Menu
                  collapse={collapsed}
                  onClickMenuItem={onClickMenuItem}
                  selectedKeys={selectedKeys}
                  openKeys={openKeys}
                  onClickSubMenu={(_, openKeys) => {
                    setOpenKeys(openKeys);
                  }}
                >
                  {renderRoutes(locale)(routes, 1)}
                </Menu>
              </div>
              <div className={styles['collapse-btn']} onClick={toggleCollapse}>
                {collapsed ? <IconMenuUnfold /> : <IconMenuFold />}
              </div>
            </Sider>
          )}
          <Layout className={styles['layout-content']} style={paddingStyle}>
            <div className={styles['layout-content-wrapper']}>
              {!!breadcrumb.length && (
                <div className={styles['layout-breadcrumb']}>
                  <Breadcrumb>
                    {breadcrumb.map((node, index) => (
                      <Breadcrumb.Item key={index}>
                        {typeof node === 'string' ? locale[node] || node : node}
                      </Breadcrumb.Item>
                    ))}
                  </Breadcrumb>
                </div>
              )}
              <Content>
                <Switch>
                  {flattenRoutes.map((route, index) => {
                    return (
                      <Route
                        key={index}
                        path={`/${route.key}`}
                        component={route.component}
                      />
                    );
                  })}
                  <Route exact path="/">
                    <Redirect to={`/${defaultRoute}`} />
                  </Route>
                  <Route
                    path="*"
                    render={(props) => {
                      // 如果用户信息正在加载，显示加载状态而不是403页面
                      if (userLoading) {
                        return (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: '60vh',
                            }}
                          >
                            <Spin tip="加载中..." />
                          </div>
                        );
                      }

                      // 用户信息已加载，显示403页面（带重试机制）
                      const Exception403 = lazyload(
                        () => import('./pages/exception/403')
                      );
                      return <Exception403 {...props} />;
                    }}
                  />
                </Switch>
              </Content>
            </div>
            {showFooter && <Footer />}
          </Layout>
        </Layout>
      )}
    </Layout>
  );
}

export default PageLayout;
